import BackofficeLayout from '@/components/layouts/BackofficeLayout'

// React
import { useState, useEffect } from 'react'

import dayjs from 'dayjs'

// Mantine
import { Anchor, Button } from '@mantine/core'
import LocationSelect from '@/components/ui/LocationSelect'

// Next
import { Link } from 'react-router-dom'

// Axios
import axios from 'axios'
import { useAxiosApi } from '@/hooks/useAxiosApi'

// UI
import DispatchDateEdit from '@/components/fulfillment_orders/DispatchDateEdit'
import Breadcrumbs from '@/components/ui/Breadcrumbs'

// Lodash
import { groupBy, orderBy, sumBy } from 'lodash'

export default function FulfillmentOrdersDispatch() {
    // Location select
    const [location, setLocation] = useState('1')

    const locationSelect = (
        <LocationSelect
            withAll={true}
            value={location}
            onChange={setLocation}
        />
    )

    // Load components with dates
    const [components, setComponents] = useState(null)
    const [fullfillmentOrdersDates, setFullfillmentOrdersDates] = useState(null)

    // Axios
    const { requestGet, requestPost } = useAxiosApi()

    function timeout(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms))
    }

    const fetchFullfillmentOrdersDates = async () => {
        const endpoint = '/fulfillment-orders-dates'

        const params = location === 'all' ? {} : { location_id: location }

        await timeout(1000)
        const { data } = await requestGet(endpoint, params)

        setFullfillmentOrdersDates(data)
    }

    useEffect(() => {
        const fetchComponents = async () => {
            const { data } = await requestGet('/components/list', {
                with_dispatch_date: true,
            })

            setComponents(data)
        }

        fetchComponents()
        fetchFullfillmentOrdersDates()
    }, [])

    // Refetch fullfillment orders dates when location changes
    useEffect(() => {
        fetchFullfillmentOrdersDates()
    }, [location])

    // Group components by date

    const componentsByDateObj = groupBy(components, (component) => {
        return component.dispatch_date
    })

    const dates = Object.entries(
        groupBy(fullfillmentOrdersDates, (line) => line.fulfill_at)
    ).map((date) => {
        return {
            date: date[0],
            items: date[1],
        }
    })

    const sortedDates = orderBy(dates, ['date'], ['asc'])

    const componentsByDate = Object.entries(componentsByDateObj).map(
        ([date, components]) => {
            return {
                date,
                components,
            }
        }
    )

    // Breadcrumbs
    const breadcrumbs = [
        {
            label: 'Dates de préparation',
        },
    ]

    return (
        <div>
            <Breadcrumbs
                breadcrumbs={breadcrumbs}
                rightSection={locationSelect}
            />

            <div className="grid grid-cols-2 w-full p-5 gap-5">
                <div className="flex flex-col gap-y-3">
                    {!fullfillmentOrdersDates && (
                        <div className="flex flex-col gap-y-3">
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                            <div className="h-32 w-full bg-gray-200 rounded-lg animate-pulse" />
                        </div>
                    )}

                    {sortedDates &&
                        sortedDates.map((dateLine) => {
                            const sum = sumBy(
                                dateLine.items,
                                'total_remaining_quantity'
                            )

                            const items = orderBy(
                                dateLine.items,
                                'total_remaining_quantity',
                                'desc'
                            )

                            return (
                                <div
                                    className="p-5 bg-white rounded-sm border flex flex-col"
                                    key={dateLine.date}
                                >
                                    <div className="flex justify-between items-center">
                                        <div className="flex flex-col">
                                            <span className="font-medium text-orange-500">
                                                {dateLine.date !== 'null'
                                                    ? dayjs(
                                                          dateLine.date
                                                      ).format('LL')
                                                    : 'Date à fixer'}
                                            </span>

                                            <span className="text-gray-400 text-sm">
                                                {sum} produit(s) à préparer
                                            </span>
                                        </div>

                                        <DispatchDateEdit
                                            location_id={location}
                                            fullfillmentOrdersDate={dateLine}
                                            onDateChange={() => {
                                                fetchFullfillmentOrdersDates()
                                            }}
                                        />
                                    </div>

                                    <div className="flex flex-col items-start mt-3">
                                        {items.map((item, index) => {
                                            let name = `${item.shopify_product_title} - ${item.shopify_variant_title}`
                                            let url = `/products/shopify/${item.shopify_product_id}`

                                            if (item.component_id) {
                                                name = `${item.component_category_name} - ${item.component_name}`
                                                url = `/components/${item.component_id}`
                                            }

                                            return (
                                                <div
                                                    key={index}
                                                    className="w-full flex justify-between"
                                                >
                                                    <Anchor
                                                        component={Link}
                                                        to={url}
                                                    >
                                                        {name}
                                                    </Anchor>

                                                    <span>
                                                        ×
                                                        {
                                                            item.total_remaining_quantity
                                                        }
                                                    </span>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )
                        })}
                </div>

                <div className="">
                    {components && (
                        <div className="p-5 bg-white rounded-sm border flex flex-col">
                            <span className="font-medium">
                                Dates des composants
                            </span>

                            <span className="text-sm text-gray-400">
                                Composants avec des dates de préparation fixées
                                pour les préparations entrantes
                            </span>

                            <div className="flex flex-col gap-3 mt-5">
                                {componentsByDate.map(
                                    ({ date, components }) => {
                                        components = components
                                            .map((c) => {
                                                return {
                                                    ...c,
                                                    full_name_fr:
                                                        c.component_category
                                                            .name_fr +
                                                        ' — ' +
                                                        c.name,
                                                }
                                            })
                                            .sort((a, b) =>
                                                a.full_name_fr.localeCompare(
                                                    b.full_name_fr
                                                )
                                            )

                                        return (
                                            <div
                                                className="flex items-start flex-col"
                                                key={date}
                                            >
                                                <span className="text-orange-500">
                                                    {dayjs(date).format('LL')}
                                                </span>

                                                {components.map((component) => (
                                                    <Anchor
                                                        component={Link}
                                                        key={component?.id}
                                                        to={
                                                            '/components/' +
                                                            component?.id
                                                        }
                                                    >
                                                        {component?.full_name_fr}
                                                    </Anchor>
                                                ))}
                                            </div>
                                        )
                                    }
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    )
}
